import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { practiceClueHolders, practiceStars, type Role } from '@il-colpo/engine';
import { trainingKey } from '@il-colpo/shared/progress';
import { stars } from './format.ts';
import { submitTrainingRecord } from '../meta/trainingSync.ts';
import { useProgressStore } from '../state/progressStore.ts';
import { Badge, Button, IconButton, Modal, Panel, ProgressBar } from '../ui/index.ts';
import type { PracticeClient } from '../local/practiceSession.ts';
import { alarmPercent, clock } from './format.ts';
import { GameProvider, useGame } from './GameContext.tsx';
import { useAutoPause } from './useAutoPause.ts';
import { useGameAudio } from './useGameAudio.ts';
import { PUZZLE_PANELS } from './puzzles/registry.ts';
import { useTrainingClient, useTrainingStore } from './trainingStore.ts';
import puzzleStyles from './puzzles/puzzles.module.css';
import styles from './TrainingScreen.module.css';

/** One puzzle card: the panel of the viewing seat, as the heist would draw it. */
function PracticeCard({ lens }: { lens: boolean }) {
  const { t } = useTranslation('training');
  const { view, g, role } = useGame();
  const entry = view.exec?.puzzles[0];
  if (!entry) return null;
  const Panel_ = PUZZLE_PANELS[entry.type];
  return (
    <Panel
      as="article"
      tone={lens ? 'night' : 'lamp'}
      className={puzzleStyles['card']}
      aria-label={lens ? g(`roles.${role}`) : t('yours')}
    >
      <header className={puzzleStyles['cardHead']}>
        <h3 className={puzzleStyles['cardTitle']}>
          {lens ? g(`roles.${role}`) : g(`puzzles.${entry.type}.name`)}
        </h3>
        {lens ? null : <Badge tone="cyan">{t('yours')}</Badge>}
      </header>
      {lens ? null : <p className={puzzleStyles['goal']}>{g(`puzzles.${entry.type}.goal`)}</p>}
      <Panel_ puzzleId={entry.id} type={entry.type} v={entry.view} lens={lens} />
    </Panel>
  );
}

function Verdict({ client }: { client: PracticeClient }) {
  const { t } = useTranslation('training');
  const { g } = useGame();
  const navigate = useNavigate();
  const s = client.snapshot();
  const start = useTrainingStore((st) => st.start);
  const setup = useTrainingStore((st) => st.setup);
  const leave = useTrainingStore((st) => st.leave);
  const record = useProgressStore((st) => st.recordTraining);
  const count = practiceStars(s);
  const won = s.status === 'won';
  // The record as it was before this run, read once when the verdict appears.
  const [before] = useState(
    () =>
      useProgressStore.getState().progress.training[trainingKey(s.options.type, s.options.level)],
  );
  const fresh = won && (!before || s.clockMs < before.bestMs);
  const noted = useRef(false);

  useEffect(() => {
    if (noted.current) return;
    noted.current = true;
    record(s.options.type, s.options.level, { ms: s.clockMs, stars: count });
    // The record as it is now goes to the board, when the player has an account (best effort).
    if (won) void submitTrainingRecord(s.options.type, s.options.level);
  }, [s, count, record, won]);

  return (
    <Panel tone={won ? 'blueprint' : 'night'} className={styles['verdict']} role="status">
      <h2 className={styles['verdictTitle']}>
        {won ? t('verdict.won') : t(`verdict.${s.lostBy ?? 'time'}`)}
      </h2>
      {won ? (
        <p className={styles['stars']} aria-label={t('starsLabel', { count })}>
          {stars(count)}
        </p>
      ) : null}
      {fresh ? <Badge tone="amber">{t('result.newRecord')}</Badge> : null}
      <dl className={styles['facts']}>
        <dt>{t('result.time')}</dt>
        <dd>{clock(s.clockMs)}</dd>
        <dt>{t('result.par')}</dt>
        <dd>{clock(s.parMs)}</dd>
        <dt>{t('result.errors')}</dt>
        <dd>{s.errors}</dd>
        <dt>{t('result.alarm')}</dt>
        <dd>{`${alarmPercent(s.alarm)}%`}</dd>
      </dl>
      <p className={puzzleStyles['muted']}>{g(`puzzles.${s.options.type}.name`)}</p>
      <div className={styles['actions']}>
        <Button
          icon="play"
          onClick={() => {
            if (setup) void start({ ...setup, seed: undefined });
          }}
        >
          {t('again')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            leave();
            void navigate('/play/training');
          }}
        >
          {t('another')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            leave();
            void navigate('/play');
          }}
        >
          {t('menu')}
        </Button>
      </div>
    </Panel>
  );
}

function Title({ level }: { level: number }) {
  const { t } = useTranslation('training');
  const { view, g } = useGame();
  const type = view.exec?.puzzles[0]?.type;
  return (
    <h1 className={styles['title']}>
      {type ? t('hudTitle', { puzzle: g(`puzzles.${type}.name`), level }) : null}
    </h1>
  );
}

function TrainingBody({ client }: { client: PracticeClient }) {
  const { t } = useTranslation('training');
  const { t: tg } = useTranslation('game');
  const navigate = useNavigate();
  const leave = useTrainingStore((s) => s.leave);
  const s = client.snapshot();
  const view = client.view();
  const over = s.status !== 'running';
  const holders: Role[] = practiceClueHolders(s);
  useGameAudio(view, over ? 'off' : 'job');
  useAutoPause(client, !over);

  return (
    <div className={styles['screen']}>
      <header className={styles['hud']}>
        <IconButton
          icon="back"
          label={tg('hud.leave')}
          onClick={() => {
            leave();
            void navigate('/play/training');
          }}
        />
        <GameProvider client={client} view={view}>
          <Title level={s.options.level} />
        </GameProvider>
        <div className={styles['gauges']}>
          <div className={styles['timer']}>
            <span className="visually-hidden">{t('timeLeft')}</span>
            <span role="timer">{clock(view.exec?.timerMs ?? 0)}</span>
          </div>
          <ProgressBar
            value={alarmPercent(s.alarm)}
            label={tg('hud.alarm')}
            tone="alarm"
            showValue
          />
        </div>
        <IconButton
          icon="pause"
          label={client.paused() ? tg('hud.resume') : tg('hud.pause')}
          onClick={() => client.setPaused(!client.paused())}
        />
      </header>
      <main id="main" tabIndex={-1} className={styles['body']}>
        {over ? (
          <GameProvider client={client} view={view}>
            <Verdict client={client} />
          </GameProvider>
        ) : null}
        <div className={styles['cards']} inert={over}>
          <GameProvider client={client} view={view}>
            <PracticeCard lens={false} />
          </GameProvider>
          <section className={styles['team']} aria-label={t('team')}>
            <h2 className={styles['teamTitle']}>{t('team')}</h2>
            <p className={puzzleStyles['muted']}>
              {holders.length > 0 ? t('teamHint') : t('teamNothing')}
            </p>
            {holders.map((role) => (
              <GameProvider key={role} client={client} view={client.viewAs(role)} asRole={role}>
                <PracticeCard lens />
              </GameProvider>
            ))}
          </section>
        </div>
      </main>
      <Modal
        open={client.paused()}
        onClose={() => client.setPaused(false)}
        title={tg('pause.title')}
        actions={<Button onClick={() => client.setPaused(false)}>{tg('pause.resume')}</Button>}
      >
        <p>{tg('pause.body')}</p>
      </Modal>
    </div>
  );
}

/** The training route: one puzzle on its own, with every role's clues on the table. */
export function TrainingScreen() {
  const client = useTrainingClient();
  const starting = useTrainingStore((s) => s.starting);
  const navigate = useNavigate();

  useEffect(() => {
    if (!client && !starting) void navigate('/play/training', { replace: true });
  }, [client, starting, navigate]);

  if (!client) return null;
  return <TrainingBody client={client} />;
}
